// The header card a project page and an agreement page share: the vendor's
// logo on the left, then the name over the vendor line and the fields the
// page is about, spread over the row.

import { FC, ReactNode } from "react";
import { clsx } from "clsx";
import { Card } from "../../../../design-system";
import { VendorLogo } from "../../../project/components/VendorLogo";
import type { UsageVendor } from "../model/types";
import classes from "./HeaderCard.module.less";

/** A labelled value of the header; sized to its content unless a bar inside needs a width. */
export const Field: FC<{ label?: string; width?: number; children: ReactNode }> = ({ label, width, children }) => (
    <div className={clsx(classes.field, !label && classes.fieldUnlabelled)} style={{ width }}>
        {label && <div className={classes.fieldLabel}>{label}</div>}
        <div className={classes.fieldValue}>{children}</div>
    </div>
);

/** A person's name in the owner field, in the link color, on the same line as the values around it. */
export const OwnerName: FC<{ name: string }> = ({ name }) => <span className={classes.owner}>{name}</span>;

export const HeaderCard: FC<{
    vendor: UsageVendor;
    /** What the logo stands for: the deal's name on a project, the agreement's on its page. */
    logoFor: string;
    name: ReactNode;
    /** The line under the name: the vendor, and the agreement when there is one. */
    detail: ReactNode;
    /**
     * "spread": the fields take the width they need, spread over the row.
     * "columns": the name takes the slack and the fields keep the widths they
     * are given, so cards stacked on a page line up.
     */
    align?: "spread" | "columns";
    /** Buttons at the card's right edge. */
    actions?: ReactNode;
    className?: string;
    children: ReactNode;
}> = ({ vendor, logoFor, name, detail, align = "spread", actions, className, children }) => (
    <Card className={clsx(classes.card, className)}>
        <VendorLogo vendorHero={vendor.hero} vendorName={vendor.name} projectName={logoFor} />
        <div className={classes.content}>
            <div className={clsx(classes.fieldRow, align === "columns" && classes["fieldRow--columns"])}>
                <Field>
                    <div className={classes.name}>{name}</div>
                    <div className={classes.vendor}>{detail}</div>
                </Field>
                {children}
                {actions && <div className={classes.actions}>{actions}</div>}
            </div>
        </div>
    </Card>
);
